import {
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ doc }: { doc: DocumentAsset }) {
  if (doc.status === 'PENDING') {
    return (
      <Chip
        size="small"
        icon={<CircularProgress size={12} sx={{ color: 'inherit' }} />}
        label="Processing…"
        color="info"
      />
    );
  }
  if (doc.status === 'FAILED') {
    return <Chip size="small" icon={<ErrorIcon />} label="Processing failed" color="error" />;
  }
  if (doc.pageCount) {
    return (
      <Chip
        size="small"
        label={`${doc.pageCount} page${doc.pageCount === 1 ? '' : 's'}`}
        variant="outlined"
      />
    );
  }
  return <Chip size="small" label="Ready" variant="outlined" />;
}

/**
 * A document has no real preview image (unlike a video's poster frame), so this is a plain
 * icon rather than a tall placeholder box pretending to be a thumbnail.
 */
function DocumentIcon({ doc }: { doc: DocumentAsset }) {
  return (
    <DescriptionIcon
      sx={{
        fontSize: 28,
        flexShrink: 0,
        color: doc.status === 'FAILED' ? 'error.light' : Palette.default.disabledText,
      }}
    />
  );
}

function DocumentActions({
  doc,
  onDelete,
  onEdit,
  onImport,
}: {
  doc: DocumentAsset;
  onDelete?: () => void;
  onEdit?: () => void;
  onImport?: () => void;
}) {
  if (!onImport && !onEdit && !onDelete) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
      {onImport && doc.status === 'READY' && (
        <Tooltip title="Import from document">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onImport();
            }}
            aria-label={`Import from ${doc.title}`}
          >
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onEdit && (
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          aria-label={`Edit ${doc.title}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {onDelete && (
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label={`Delete ${doc.title}`}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

export default function DocumentLibraryCard({
  doc,
  onDelete,
  onEdit,
  onImport,
  fluid = false,
}: {
  doc: DocumentAsset;
  onDelete?: () => void;
  onEdit?: () => void;
  onImport?: () => void;
  fluid?: boolean;
}) {
  return (
    <Box
      sx={{
        width: fluid ? '100%' : 220,
        // This app has no global box-sizing:border-box reset, so width:100% plus this box's
        // own border+padding would otherwise add on top of it instead of being drawn inside
        // it -- rendering the whole box (border included) wider than its container rather
        // than just letting its content overflow (which overflow:hidden alone can't fix).
        boxSizing: 'border-box',
        flexShrink: 0,
        border: '1px solid',
        borderColor: Palette.default.border,
        borderRadius: RADIUS.section,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
      }}
    >
      <DocumentIcon doc={doc} />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={doc.title}>
          {doc.title}
        </Typography>
        {doc.originalFilename && (
          <Typography variant="caption" color="text.secondary" noWrap title={doc.originalFilename}>
            {doc.originalFilename}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusBadge doc={doc} />
          {doc.status === 'READY' && doc.fileSizeBytes && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(doc.fileSizeBytes)}
            </Typography>
          )}
        </Box>
        {doc.status === 'FAILED' && doc.errorMessage && (
          <Typography variant="caption" color="error.main">
            {doc.errorMessage}
          </Typography>
        )}
      </Box>
      <DocumentActions doc={doc} onDelete={onDelete} onEdit={onEdit} onImport={onImport} />
    </Box>
  );
}
